import type { DiscountInfo } from '@store/generated/subscriptions';

import type { BundleWithStatus } from './types';

/**
 * Format a price in cents to a human-readable string.
 * e.g., 1999 -> "$19.99", 2000 -> "$20"
 */
export function formatPrice(cents: number, currency = 'usd'): string {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Get the monthly price amount in cents for a bundle, accounting for billing interval */
export function getMonthlyAmountCents(bundle: BundleWithStatus, isAnnual: boolean): number {
  return isAnnual ? Math.round(bundle.yearly_unit_amount / 12) : bundle.monthly_unit_amount;
}

/**
 * Calculate the discount amount in cents for a given subtotal.
 * Returns the amount to subtract (always positive or zero).
 *
 * For percentage discounts: applies percent_off to the subtotal.
 * For fixed discounts: amount_off is per-invoice, so for annual billing
 * we divide by 12 to get the monthly equivalent.
 */
export const calculateDiscountAmount = (
  discount: DiscountInfo | null | undefined,
  subtotalCents: number,
  billingInterval: string,
): number => {
  if (!discount) return 0;

  if (discount.percent_off) {
    return Math.round((subtotalCents * discount.percent_off) / 100);
  }

  if (discount.amount_off) {
    // amount_off is per-invoice; for annual billing show monthly equivalent
    if (billingInterval === 'year') {
      return Math.round(discount.amount_off / 12);
    }
    return discount.amount_off;
  }

  return 0;
};

/**
 * Get human-readable duration text for a Stripe discount.
 */
export const getDiscountDurationText = (
  discount: DiscountInfo | null | undefined,
): string | null => {
  if (!discount?.duration) return null;

  switch (discount.duration) {
    case 'once':
      return null;
    case 'repeating':
      return discount.duration_in_months
        ? `Applies for ${discount.duration_in_months} months`
        : 'Applies for multiple billing cycles';
    case 'forever':
      return 'Ongoing discount';
    default:
      return null;
  }
};
